<template>
  <!-- User Avatar + PrimeVue dropdown menu -->
  <div
    id="btn-user-menu-trigger"
    class="flex items-center rounded-lg p-2 cursor-pointer transition-colors"
    @click="toggleUserMenu"
    role="button"
    aria-haspopup="true"
    :aria-expanded="userMenuOpen"
  >
    <Avatar :label="userInitials" shape="circle" size="normal" class="!bg-cyan-600 !text-white" />
  </div>

  <!-- PrimeVue TieredMenu (popup mode) with custom icons -->
  <TieredMenu
    ref="userMenu"
    :model="menuItems"
    popup
  >
    <!-- User profile header section -->
    <template #start>
      <div class="px-4 py-3 border-b border-[var(--p-surface-700)]">
        <div class="flex items-center gap-3">
          <Avatar :label="userInitials" shape="circle" class="!bg-cyan-600 !text-white" />
          <div class="flex flex-col">
            <span class="text-white font-semibold text-sm">{{ userName }}</span>
            <span class="text-gray-400 text-xs">{{ userEmail }}</span>
          </div>
        </div>
      </div>
    </template>

    <template #item="{ item, props, hasSubmenu }">
      <!-- Separator -->
      <a v-if="item.separator"
         v-bind="props.action"
         class="p-menu-separator"
         role="separator">
      </a>

      <!-- Menu items with icons -->
      <a v-else
         v-ripple
         v-bind="props.action"
         data-telemetry-id="user-menu-item"
         class="flex items-center gap-3 px-4 py-3 cursor-pointer text-white hover:bg-[var(--p-surface-700)] transition-colors"
      >
        <!-- Icon -->
        <span v-if="item.icon" class="material-icons text-lg">{{ item.icon }}</span>

        <!-- Label -->
        <span class="flex-1">{{ item.label }}</span>
      </a>
    </template>
  </TieredMenu>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useUser } from '@clerk/vue';
import { useWorkspaceStore } from '../../stores/workspaceStore';
import { useTokenStore } from '../../composables/useTokenStore';

const workspaceStore = useWorkspaceStore();
const tokenStore = useTokenStore();

/**
 * UserMenu Component
 * 
 * Displays user avatar (trigger) with dropdown menu containing:
 * - User profile header (name + email)
 * - Language selector submenu
 * - Navigation links (Billing, Admin)
 * - Logout action
 * 
 * Uses PrimeVue Avatar and TieredMenu components.
 * Fetches user data directly from Clerk authentication.
 * 
 * The trigger shows only the avatar to save header space.
 * User identity (name + email) is displayed inside the menu header.
 * 
 * @component
 * @example
 * <UserMenu
 *   :locale="currentLocale"
 *   @logout="handleLogout"
 *   @change-language="handleLanguageChange"
 * />
 */

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// Fetch user data from Clerk
const { user } = useUser();

/**
 * Component props
 */
const props = defineProps({
  /**
   * Current locale/language code (e.g., 'it', 'en')
   * @type {String}
   */
  locale: {
    type: String,
    required: true
  }
});

/**
 * Component events
 */
const emit = defineEmits({
  /**
   * Emitted when user clicks the logout menu item
   * Parent component should handle the actual logout logic
   */
  logout: () => true,

  /**
   * Emitted when user changes the language
   * @param {string} locale - The selected locale code
   */
  changeLanguage: (locale) => typeof locale === 'string'
});

/**
 * Computed: User's display name from Clerk
 * Prioritizes firstName, then username, with 'User' as fallback
 * Note: Never contains email address (see userEmail computed)
 * @returns {string} User's display name
 */
const userName = computed(() => {
  return user.value?.firstName || user.value?.username || t('userMenu.defaultUserName');
});

/**
 * Computed: User's email address from Clerk
 * Prioritizes primaryEmailAddress structure, then direct email property
 * @returns {string} User's email address or empty string
 */
const userEmail = computed(() => {
  return user.value?.primaryEmailAddress?.emailAddress || user.value?.email || '';
});

/**
 * Computed: User's initials for avatar display
 * Intelligently extracts initials from name, or uses first letter of email if no name
 * @returns {string} 1-2 character initials (uppercase)
 */
const userInitials = computed(() => {
  const name = userName.value;

  // If no name, use first letter of email or 'U'
  if (name === t('userMenu.defaultUserName')) {
    const email = userEmail.value;
    return email ? email[0].toUpperCase() : 'U';
  }

  // Handle full names (first + last)
  const nameParts = name.trim().split(/\s+/);
  if (nameParts.length >= 2) {
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  }

  // Handle single name
  return name.substring(0, 2).toUpperCase();
});

/**
 * Computed: Whether user has admin privileges
 * Checks Clerk publicMetadata for role === 'admin'
 * @returns {boolean} True if user is admin
 */
const isAdmin = computed(() => {
  return user.value?.publicMetadata?.role === 'admin';
});

// User menu state
const userMenu = ref(null);
const userMenuOpen = ref(false);

/**
 * Menu items configuration for user dropdown
 * 
 * Structure:
 * 1. Language selector (submenu with IT/EN options - always visible at top)
 * 2. Separator
 * 3. Billing (hidden when workspace has unlimited credits)
 * 4. Admin (conditional - only if isAdmin prop is true)
 * 5. Separator
 * 6. Logout (always visible)
 * 
 * Uses Material Icons for all icons with custom template rendering.
 * Language submenu shows current language with checkmark icon.
 * TieredMenu handles submenu arrows and hover behavior automatically via v-bind="props.action".
 */
const menuItems = computed(() => {
  const items = [
    // Language selector (with nested submenu)
    {
      label: t('language.label'),
      icon: 'language',
      items: [
        {
          label: '🇮🇹 Italiano',
          icon: props.locale === 'it' ? 'check' : '',
          command: () => handleLanguageChange('it')
        },
        {
          label: '🇬🇧 English',
          icon: props.locale === 'en' ? 'check' : '',
          command: () => handleLanguageChange('en')
        }
      ]
    },
    // Separator after language selector
    { separator: true },
    // Workspace area (general + members + delete)
    {
      label: t('userMenu.workspaceSettings'),
      icon: 'tune',
      command: () => {
        if (!workspaceStore.currentWorkspaceId) return; // no active workspace (e.g. on admin pages) — avoid a vue-router "missing param workspaceId" throw
        router.push({ name: 'WorkspaceSettings', params: { workspaceId: workspaceStore.currentWorkspaceId } });
      }
    }
  ];

  // Billing — hidden for workspaces with unlimited credits or managed clients
  if (!tokenStore.store.isUnlimited && !tokenStore.store.managed) {
    items.splice(2, 0, {
      label: t('userMenu.billing'),
      icon: 'credit_card',
      command: () => {
        if (!workspaceStore.currentWorkspaceId) return; // no active workspace — avoid a vue-router "missing param workspaceId" throw
        router.push({ name: 'Billing', params: { workspaceId: workspaceStore.currentWorkspaceId } });
      }
    });
  }

  // Admin/App toggle (admins only): nell'app porta all'admin; nell'admin riporta all'app
  if (isAdmin.value) {
    const inAdmin = route.path.startsWith('/admin');
    items.push({
      label: inAdmin ? t('userMenu.application') : t('userMenu.administration'),
      icon: inAdmin ? 'apps' : 'admin_panel_settings',
      command: () => {
        router.push(inAdmin ? '/app/bots' : '/admin');
      }
    });
  }

  // Add separator before logout
  items.push({ separator: true });

  // Add logout item
  items.push({
    label: t('userMenu.logout'),
    icon: 'logout',
    command: () => {
      emit('logout');
    }
  });

  return items;
});

/**
 * Toggle user menu visibility
 * @param {Event} event - Click event from avatar trigger
 */
const toggleUserMenu = (event) => {
  userMenuOpen.value = !userMenuOpen.value;
  userMenu.value?.toggle(event);
};

/**
 * Handle language change event
 * @param {string} newLocale - The selected locale code ('it' or 'en')
 */
const handleLanguageChange = (newLocale) => {
  emit('changeLanguage', newLocale);
};
</script>
